import "../src/styles.css";
import "./style.css";

import { NARWEE_FIXTURE } from "../src/fixtures/narwee.ts";
import { mountRoofVisualizer } from "../src/roofviz/mount.ts";
import type { ProfileId } from "../src/roofviz/materials.ts";

const visualizerRoot = document.querySelector<HTMLElement>("#runs-visualizer");

if (!visualizerRoot) throw new Error("Runs visualizer root is missing.");

const visualizer = mountRoofVisualizer({
  root: visualizerRoot,
  packet: NARWEE_FIXTURE.packet,
  presentation: {
    shell: "embedded",
    footprints: NARWEE_FIXTURE.footprints,
    assumedHeights: NARWEE_FIXTURE.assumedHeights,
    homeownerNote: "Visual guide only.",
    autoRotate: true,
    autoRotateSpeed: 0.42,
    startInRoof: true,
  },
  initialSession: {
    appearance: { profile: "true_oak_corrugate", colour: "ironsand" },
    gutter: "quarter_round_150",
    fascia: "multifit_185",
    view: "perspective",
  },
});

document.querySelectorAll<HTMLButtonElement>(".material-choice").forEach((button) => {
  button.addEventListener("click", () => {
    const profile = button.dataset.profile as ProfileId | undefined;
    const colour = button.dataset.colour;
    if (!profile || !colour) return;
    visualizer.setAppearance({ profile, colour });
    document.querySelectorAll<HTMLButtonElement>(".material-choice").forEach((candidate) => {
      const active = candidate === button;
      candidate.classList.toggle("is-active", active);
      candidate.setAttribute("aria-pressed", String(active));
    });
  });
});

type WalkthroughName = "homeowner" | "roofer";

const walkthroughs: Record<WalkthroughName, string[]> = {
  homeowner: [
    new URL("./assets/app-screens/homeowner-read.png", import.meta.url).href,
    new URL("./assets/app-screens/homeowner-define.png", import.meta.url).href,
  ],
  roofer: [
    new URL("./assets/app-screens/roofer-source-4.png", import.meta.url).href,
    new URL("./assets/app-screens/roofer-source-11.png", import.meta.url).href,
  ],
};

const currentIndex: Record<WalkthroughName, number> = { homeowner: 0, roofer: 0 };

function renderWalkthrough(name: WalkthroughName, direction = 1): void {
  const screens = walkthroughs[name];
  const index = currentIndex[name];
  const stage = document.querySelector<HTMLElement>(`[data-walkthrough-screen="${name}"]`);
  const dots = document.querySelector<HTMLElement>(`[data-walkthrough-dots="${name}"]`);
  if (!stage || !dots) return;

  stage.classList.remove("is-forward", "is-backward");
  stage.classList.add(direction >= 0 ? "is-forward" : "is-backward");
  const needsBrandMask = index === 1;
  const brandMask = needsBrandMask ? `<span class="actual-screen-brand ${name}" aria-hidden="true"><img src="${new URL("./assets/roofhero-logo-dark.svg", import.meta.url).href}" alt=""></span>` : "";
  stage.innerHTML = `<div class="actual-screen"><img src="${screens[index]}" alt="${name === "homeowner" ? "RoofHero homeowner" : "RoofHero roofer"} app screen ${index + 1}">${brandMask}</div>`;
  dots.innerHTML = screens.map((_, dotIndex) => `<button type="button" aria-label="Show ${name} screen ${dotIndex + 1}" class="${dotIndex === index ? "is-active" : ""}" data-walkthrough-dot="${name}" data-index="${dotIndex}"></button>`).join("");
  dots.querySelectorAll<HTMLButtonElement>("button").forEach((dot) => {
    dot.addEventListener("click", () => {
      const next = Number(dot.dataset.index);
      const move = next >= currentIndex[name] ? 1 : -1;
      currentIndex[name] = next;
      renderWalkthrough(name, move);
    });
  });
}

(["homeowner", "roofer"] as WalkthroughName[]).forEach((name) => {
  renderWalkthrough(name);
  document.querySelector<HTMLButtonElement>(`[data-walkthrough-prev="${name}"]`)?.addEventListener("click", () => {
    currentIndex[name] = (currentIndex[name] - 1 + walkthroughs[name].length) % walkthroughs[name].length;
    renderWalkthrough(name, -1);
  });
  document.querySelector<HTMLButtonElement>(`[data-walkthrough-next="${name}"]`)?.addEventListener("click", () => {
    currentIndex[name] = (currentIndex[name] + 1) % walkthroughs[name].length;
    renderWalkthrough(name, 1);
  });
});

window.addEventListener("beforeunload", () => visualizer.destroy(), { once: true });
